import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect, useRef } from 'react';
import ModalX from '../ModalX';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import Markdown from 'react-markdown';

// Wrapper to provide a basic but styled modal dialog

// Props:
// icon: JSX.Element (optional)
// title: string (optional)
// children: JSX.Element
// showModal: boolean
// setShowModal: function

// Example usage:
// const [isFormError, setIsFormError] = useState(false);
// <BasicModal
//   icon={<ExclamationCircleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />}
//   title="Error"
//   showModal={isFormError}
//   setShowModal={setIsFormError}
// >
//   <div className="text-sm text-gray-500">
//     {errorMessage}
//   </div>
// </BasicModal>

export default function BasicModal({
  icon,
  title,
  ariaLabel,
  markdownTitle = false,
  children,
  showModal,
  setShowModal,
  iconBackground,
  initialFocusRef,
  onBackButtonClicked,
  backButtonCopy,
  dialogPadding = 'px-4 pb-4 pt-5 sm:p-6',
  maxWidth = 'sm:max-w-lg',
  maxHeight = 'max-h-[95vh]',
  overflowY = 'overflow-y-auto',
  isExitable = true,
  stickyCloseButton = false,
  flexColumn = false,
  // Passed straight to ModalX; leaving it unset keeps the inset every modal already had.
  closeButtonInset,
  // Run the enter transition on first mount. Off by default, which is every existing caller:
  // they mount closed and animate when `showModal` flips. A modal that is mounted already
  // open — one loaded on demand — would otherwise pop in with no transition at all.
  appear = false,
}) {
  const concatenateClasses = (classes) => {
    return classes.join(' ');
  };

  // On iOS, Headless UI's document-level scroll lock cancels any touchmove it
  // can't trace to the open dialog. When one dialog closes in the same update
  // another opens (e.g. the mobile nav drawer opening a modal), the dead
  // dialog's lock outlives its unmount and cancels every touch in the new
  // modal, freezing its scroll. Stopping touchmove at the panel keeps it away
  // from that handler; touches outside still reach it, so the page stays locked.
  const panelRef = useRef(null);
  useEffect(() => {
    const panel = panelRef.current;
    if (!showModal || !panel) {
      return undefined;
    }
    const keepTouchInsidePanel = (event) => event.stopPropagation();
    panel.addEventListener('touchmove', keepTouchInsidePanel, { passive: true });
    return () => panel.removeEventListener('touchmove', keepTouchInsidePanel);
  }, [showModal]);

  return (
    <Transition.Root show={showModal} as={Fragment} appear={appear}>
      <Dialog
        aria-label={ariaLabel || title || 'Modal'}
        as='div'
        className='relative z-[60] print:hidden'
        initialFocus={initialFocusRef}
        onClose={() => {
          if (isExitable) {
            setShowModal(false);
          }
        }}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-10 overflow-y-auto'>
          <div className='flex min-h-full justify-center p-4 text-center items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'>
              <Dialog.Panel
                ref={panelRef}
                className={`transform rounded-lg text-left shadow-xl transition-all ${maxHeight} w-full bg-white ${maxWidth} ${dialogPadding || ''} ${overflowY} ${flexColumn ? 'flex flex-col' : ''}`}>
                {/* when the panel scrolls, a zero-height sticky wrapper keeps the X in view */}
                {isExitable &&
                  (stickyCloseButton ? (
                    <div className='sticky top-0 z-20 h-0'>
                      <ModalX setOpen={setShowModal} inset={closeButtonInset} />
                    </div>
                  ) : (
                    <ModalX setOpen={setShowModal} inset={closeButtonInset} />
                  ))}
                <div className={flexColumn ? 'flex min-h-0 flex-1 flex-col' : undefined}>
                  {onBackButtonClicked && (
                    <div className='absolute top-6 left-4 w-fit '>
                      <button
                        className='w-fit flex flex-row items-center cursor-pointer rounded-md bg-white text-gray-500 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2'
                        onClick={onBackButtonClicked}>
                        <ArrowLeftIcon className='h-5 w-5 ' />
                        <span className='hidden sm:block text-xs'>{backButtonCopy}</span>
                        {/* Back to Preview/Search */}
                      </button>
                    </div>
                  )}
                  {icon && (
                    <div
                      className={concatenateClasses([
                        'mx-auto flex h-12 w-12 items-center justify-center rounded-full',
                        iconBackground || 'bg-green-100',
                      ])}>
                      {icon}
                    </div>
                  )}
                  {title && (
                    <div className='mt-1 text-center sm:mt-2'>
                      <Dialog.Title
                        as='h3'
                        className={`mb-2 text-base leading-6 text-gray-900 ${markdownTitle ? '' : 'font-semibold'}`}>
                        {markdownTitle ? <Markdown>{title}</Markdown> : title}
                      </Dialog.Title>
                    </div>
                  )}
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
